<script setup>
import { computed, reactive, ref } from 'vue';
import Message from './Message.vue';
import { MESSAGE_TYPES } from './constants.js';
import { useCamelCase } from 'dashboard/composables/useTransformKeys';
import { useMapGetter } from 'dashboard/composables/store.js';
import MessageApi from 'dashboard/api/inbox/message.js';
import ForwardDialog from 'dashboard/modules/conversations/components/ForwardDialog.vue';
import { useMessageForwardSelection } from 'dashboard/composables/useMessageForwardSelection';

/**
 * Props definition for the component
 * @typedef {Object} Props
 * @property {Array} readMessages - Array of read messages
 * @property {Array} unReadMessages - Array of unread messages
 * @property {Number} currentUserId - ID of the current user
 * @property {Boolean} isAnEmailChannel - Whether this is an email channel
 * @property {Object} inboxSupportsReplyTo - Inbox reply support configuration
 * @property {Array} messages - Array of all messages [These are not in camelcase]
 */
const props = defineProps({
  currentUserId: {
    type: Number,
    required: true,
  },
  firstUnreadId: {
    type: Number,
    default: null,
  },
  isAnEmailChannel: {
    type: Boolean,
    default: false,
  },
  inboxSupportsReplyTo: {
    type: Object,
    default: () => ({ incoming: false, outgoing: false }),
  },
  messages: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['retry']);

const WA_GREEN = '#00a884';
const showForwardDialog = ref(false);
const { state: selection, toggle, isSelected, clear } =
  useMessageForwardSelection();

const selectionConversationId = computed(
  () => allMessages.value?.[0]?.conversationId ?? null
);
const selectionActive = computed(
  () =>
    selection.active &&
    selection.conversationId != null &&
    selection.conversationId === selectionConversationId.value
);
const selectedCount = computed(() => selection.ids.length);

const isSelectable = message => {
  const hasText = !!message.content;
  const hasAttachments = !!(message.attachments && message.attachments.length);
  const isDeleted = !!(
    message.contentAttributes && message.contentAttributes.deleted
  );
  return (hasText || hasAttachments) && !isDeleted;
};

const onRowClick = (event, message) => {
  if (!selectionActive.value || !isSelectable(message)) return;
  event.preventDefault();
  event.stopPropagation();
  toggle(message.id);
};

const openForwardDialog = () => {
  if (!selection.ids.length) return;
  showForwardDialog.value = true;
};
const closeForwardDialog = () => {
  showForwardDialog.value = false;
};
const onForwardSuccess = () => clear();
const cancelSelection = () => clear();

const allMessages = computed(() => {
  return useCamelCase(props.messages, {
    deep: true,
    stopPaths: ['content_attributes.translations'],
  });
});

const currentChat = useMapGetter('getSelectedChat');

// Cache for fetched reply messages to avoid duplicate API calls
const fetchedReplyMessages = reactive(new Map());

/**
 * Fetches a specific message from the API by trying to get messages around it
 * @param {number} messageId - The ID of the message to fetch
 * @param {number} conversationId - The ID of the conversation
 * @returns {Promise<Object|null>} - The fetched message or null if not found/error
 */
const fetchReplyMessage = async (messageId, conversationId) => {
  // Return cached result if already fetched
  if (fetchedReplyMessages.has(messageId)) {
    return fetchedReplyMessages.get(messageId);
  }

  try {
    const response = await MessageApi.getPreviousMessages({
      conversationId,
      before: messageId + 100,
      after: messageId - 100,
    });

    const messages = response.data?.payload || [];
    const targetMessage = messages.find(msg => msg.id === messageId);

    if (targetMessage) {
      const camelCaseMessage = useCamelCase(targetMessage);
      fetchedReplyMessages.set(messageId, camelCaseMessage);
      return camelCaseMessage;
    }

    // Cache null result to avoid repeated API calls
    fetchedReplyMessages.set(messageId, null);
    return null;
  } catch (error) {
    fetchedReplyMessages.set(messageId, null);
    return null;
  }
};

/**
 * Determines if a message should be grouped with the next message
 * @param {Number} index - Index of the current message
 * @param {Array} searchList - Array of messages to check
 * @returns {Boolean} - Whether the message should be grouped with next
 */
const shouldGroupWithNext = (index, searchList) => {
  if (index === searchList.length - 1) return false;

  const current = searchList[index];
  const next = searchList[index + 1];

  if (next.status === 'failed') return false;

  const nextSenderId = next.senderId ?? next.sender?.id;
  const currentSenderId = current.senderId ?? current.sender?.id;
  const hasSameSender = nextSenderId === currentSenderId;

  const nextMessageType = next.messageType;
  const currentMessageType = current.messageType;

  const areBothTemplates =
    nextMessageType === MESSAGE_TYPES.TEMPLATE &&
    currentMessageType === MESSAGE_TYPES.TEMPLATE;

  if (!hasSameSender || areBothTemplates) return false;

  if (currentMessageType !== nextMessageType) return false;

  // Check if messages are in the same minute by rounding down to nearest minute
  return Math.floor(next.createdAt / 60) === Math.floor(current.createdAt / 60);
};

/**
 * Gets the message that was replied to
 * @param {Object} parentMessage - The message containing the reply reference
 * @returns {Object|null} - The message being replied to, or null if not found
 */
const getInReplyToMessage = parentMessage => {
  if (!parentMessage) return null;

  const inReplyToMessageId =
    parentMessage.contentAttributes?.inReplyTo ??
    parentMessage.content_attributes?.in_reply_to;

  if (!inReplyToMessageId) return null;

  // Try to find in current messages first
  let replyMessage = props.messages?.find(msg => msg.id === inReplyToMessageId);

  // Then try store messages
  if (!replyMessage && currentChat.value?.messages) {
    replyMessage = currentChat.value.messages.find(
      msg => msg.id === inReplyToMessageId
    );
  }

  // Then check fetch cache
  if (!replyMessage && fetchedReplyMessages.has(inReplyToMessageId)) {
    replyMessage = fetchedReplyMessages.get(inReplyToMessageId);
  }

  // If still not found and we have conversation context, fetch it
  if (!replyMessage && currentChat.value?.id) {
    fetchReplyMessage(inReplyToMessageId, currentChat.value.id);
    return null; // Let UI handle loading state
  }

  return replyMessage ? useCamelCase(replyMessage) : null;
};
</script>

<template>
  <ul class="px-4 koutis-wa-chat-bg">
    <slot name="beforeAll" />
    <template v-for="(message, index) in allMessages" :key="message.id">
      <slot
        v-if="firstUnreadId && message.id === firstUnreadId"
        name="unreadBadge"
      />
      <div
        class="relative"
        :class="{
          'cursor-pointer': selectionActive && isSelectable(message),
          'rounded-lg bg-n-slate-3/60':
            selectionActive && isSelected(message.id),
        }"
        @click.capture="onRowClick($event, message)"
      >
        <span
          v-if="selectionActive && isSelectable(message)"
          class="absolute z-20 flex items-center justify-center w-5 h-5 rounded-full ltr:left-0 rtl:right-0 top-1/2 -translate-y-1/2"
          :style="
            isSelected(message.id)
              ? `background:${WA_GREEN};color:#fff`
              : 'border:1.5px solid var(--s-300)'
          "
        >
          <span v-if="isSelected(message.id)" class="w-3 h-3 i-lucide-check" />
        </span>
        <Message
          v-bind="message"
          :is-email-inbox="isAnEmailChannel"
          :in-reply-to="getInReplyToMessage(message)"
          :group-with-next="shouldGroupWithNext(index, allMessages)"
          :inbox-supports-reply-to="inboxSupportsReplyTo"
          :current-user-id="currentUserId"
          data-clarity-mask="True"
          @retry="emit('retry', message)"
        />
      </div>
    </template>
    <slot name="after" />
    <Teleport to="body">
    <div
      v-if="selectionActive"
      class="fixed z-50 flex items-center gap-3 px-4 py-2 -translate-x-1/2 border rounded-full shadow-xl bottom-24 left-1/2 bg-n-background border-n-weak"
    >
      <span class="text-sm text-n-slate-11">
        {{
          $t('CONVERSATION.CONTEXT_MENU.FORWARD.SELECTED_COUNT', {
            count: selectedCount,
          })
        }}
      </span>
      <button
        type="button"
        class="text-sm text-n-slate-11 hover:underline"
        @click="cancelSelection"
      >
        {{ $t('CONVERSATION.CONTEXT_MENU.FORWARD.CANCEL_SELECTION') }}
      </button>
      <button
        type="button"
        :disabled="selectedCount === 0"
        class="flex items-center gap-2 px-4 py-1.5 text-sm text-white rounded-full disabled:opacity-50"
        :style="`background:${WA_GREEN}`"
        @click="openForwardDialog"
      >
        <span class="w-4 h-4 i-lucide-share" />
        {{ $t('CONVERSATION.CONTEXT_MENU.FORWARD.MENU_LABEL') }}
      </button>
    </div>
  </Teleport>
  <Teleport to="body">
    <ForwardDialog
      v-if="showForwardDialog"
      :source-conversation-id="selectionConversationId"
      :message-ids="selection.ids"
      :on-close="closeForwardDialog"
      :on-success="onForwardSuccess"
    />
  </Teleport>
  </ul>
</template>
